import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Building2,
  Save,
  CheckCircle2,
  Factory,
  Handshake,
  Upload,
  Image as ImageIcon,
  X,
  Info,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

const DEFAULT_FORM = {
  // ISP Induk
  nama_isp: "NUSA NET FIBER",
  badan_usaha: "PT. NUSA MEDIA DIGITAL",
  alamat: "Jl. Telekomunikasi No. 88, Bandung Digital Valley",
  telepon: "022-87654321",
  whatsapp_cs: "6281234567890",
  email: "cs@nusanetfiber.id",
  website: "https://nusanetfiber.id",
  npwp: "01.234.567.8-429.000",
  ppn_persen: 11.0,
  invoice_footer: "Terima kasih atas kepercayaan Anda menggunakan layanan internet cepat kami.",
  logo: "",
  // Reseller / Mitra
  reseller_enabled: false,
  reseller_nama: "",
  reseller_badan_usaha: "",
  reseller_alamat: "",
  reseller_telepon: "",
  reseller_whatsapp: "",
  reseller_email: "",
  reseller_website: "",
  reseller_npwp: "",
  reseller_logo: "",
};

const inputCls = "w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-white";

export const PengaturanPerusahaanPage = () => {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");
  const [activeTab, setActiveTab] = useState("induk");
  const [logoError, setLogoError] = useState("");

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${BACKEND_URL}/api/pengaturan/perusahaan`, { withCredentials: true });
        if (res.data) setForm({ ...DEFAULT_FORM, ...res.data });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleLogoUpload = (key, file) => {
    setLogoError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) { setLogoError("File harus berupa gambar (PNG/JPG/SVG)"); return; }
    if (file.size > 1024 * 1024) { setLogoError("Ukuran logo maksimal 1MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setField(key, reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${BACKEND_URL}/api/pengaturan/perusahaan`, form, { withCredentials: true });
      setNotification("Pengaturan perusahaan berhasil diperbarui!");
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal menyimpan pengaturan");
    }
  };

  const LogoUploader = ({ label, valueKey, accent, testid }) => (
    <div className="flex items-center gap-4 p-4 rounded-xl bg-slate-900 border border-slate-800">
      <div className="w-20 h-20 rounded-xl bg-slate-950 border border-slate-700 flex items-center justify-center overflow-hidden flex-shrink-0">
        {form[valueKey] ? (
          <img src={form[valueKey]} alt={label} className="w-full h-full object-contain" data-testid={`${testid}-preview`} />
        ) : (
          <ImageIcon className="w-7 h-7 text-slate-600" />
        )}
      </div>
      <div className="flex-1">
        <p className="text-xs font-bold text-white mb-1">{label}</p>
        <p className="text-[10px] text-slate-500 mb-2">PNG / JPG / SVG, maks 1MB. Tampil pada kop invoice.</p>
        <div className="flex gap-2">
          <label className={`px-3 py-1.5 rounded-lg text-[11px] font-bold flex items-center gap-1.5 cursor-pointer bg-${accent}-500/20 text-${accent}-300 hover:bg-${accent}-500/30`}>
            <Upload className="w-3.5 h-3.5" /> Upload Logo
            <input type="file" accept="image/*" className="hidden" data-testid={`${testid}-input`} onChange={(e) => handleLogoUpload(valueKey, e.target.files?.[0])} />
          </label>
          {form[valueKey] && (
            <button type="button" onClick={() => setField(valueKey, "")} data-testid={`${testid}-remove`} className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-400 hover:text-rose-300 text-[11px] font-bold flex items-center gap-1">
              <X className="w-3.5 h-3.5" /> Hapus
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="pengaturan-perusahaan-page">
      <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-500/20 text-teal-400 flex items-center justify-center">
            <Building2 className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Pengaturan Perusahaan (ISP Profile)</h1>
            <p className="text-xs text-slate-400">Identitas ISP Induk & Perusahaan Reseller/Mitra untuk kop invoice</p>
          </div>
        </div>
      </div>

      {notification && (
        <div className="p-3 bg-teal-500/10 border border-teal-500/30 text-teal-300 rounded-xl text-xs flex items-center gap-2" data-testid="save-perusahaan-notification">
          <CheckCircle2 className="w-4 h-4 text-teal-400" />
          <span>{notification}</span>
        </div>
      )}

      <form onSubmit={handleSave} className="rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-slate-800 bg-slate-950/60 px-2" data-testid="perusahaan-tabs">
          <button
            type="button"
            onClick={() => setActiveTab("induk")}
            data-testid="tab-induk"
            className={`px-4 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "induk" ? "border-teal-400 text-teal-300" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <Factory className="w-3.5 h-3.5" /> ISP Induk
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("reseller")}
            data-testid="tab-reseller"
            className={`px-4 py-3 text-xs font-bold flex items-center gap-2 border-b-2 transition-colors ${
              activeTab === "reseller" ? "border-indigo-400 text-indigo-300" : "border-transparent text-slate-400 hover:text-slate-200"
            }`}
          >
            <Handshake className="w-3.5 h-3.5" /> Reseller / Mitra
          </button>
        </div>

        <div className="p-6 space-y-4">
          {logoError && (
            <div className="p-2.5 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-300 text-[11px] flex items-center gap-2" data-testid="logo-error">
              <Info className="w-3.5 h-3.5" /> {logoError}
            </div>
          )}

          {/* TAB ISP INDUK */}
          {activeTab === "induk" && (
            <div className="space-y-4" data-testid="tab-induk-content">
              <p className="text-[11px] text-slate-400 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 text-teal-400" />
                Data penyedia internet utama (contoh: <b className="text-slate-200">PT. ABC</b>) — tampil sebagai identitas resmi di invoice.
              </p>

              <LogoUploader label="Logo ISP Induk" valueKey="logo" accent="teal" testid="induk-logo" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Nama Brand ISP *</label>
                  <input type="text" required data-testid="input-isp-name" value={form.nama_isp} onChange={(e) => setField("nama_isp", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Badan Usaha / PT / CV *</label>
                  <input type="text" required data-testid="input-isp-badan" value={form.badan_usaha} onChange={(e) => setField("badan_usaha", e.target.value)} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Alamat Kantor Utama *</label>
                <textarea rows="2" required data-testid="input-isp-alamat" value={form.alamat} onChange={(e) => setField("alamat", e.target.value)} className={inputCls} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">No. Telepon Kantor</label>
                  <input type="text" data-testid="input-isp-telepon" value={form.telepon} onChange={(e) => setField("telepon", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">No. WhatsApp CS</label>
                  <input type="text" data-testid="input-isp-wa" value={form.whatsapp_cs} onChange={(e) => setField("whatsapp_cs", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Email Resmi</label>
                  <input type="email" data-testid="input-isp-email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Website</label>
                  <input type="text" data-testid="input-isp-website" value={form.website} onChange={(e) => setField("website", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">NPWP</label>
                  <input type="text" data-testid="input-isp-npwp" value={form.npwp} onChange={(e) => setField("npwp", e.target.value)} className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Catatan Kaki Invoice (Footer)</label>
                <input type="text" data-testid="input-isp-footer" value={form.invoice_footer} onChange={(e) => setField("invoice_footer", e.target.value)} className={inputCls} />
              </div>
            </div>
          )}

          {/* TAB RESELLER */}
          {activeTab === "reseller" && (
            <div className="space-y-4" data-testid="tab-reseller-content">
              <div className="flex items-center justify-between p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/30">
                <div className="flex items-center gap-2">
                  <Handshake className="w-4 h-4 text-indigo-300" />
                  <div>
                    <p className="text-xs font-bold text-white">Aktifkan Perusahaan Reseller / Mitra</p>
                    <p className="text-[10px] text-slate-400">Contoh: <b className="text-slate-200">PT. DEF</b> sebagai mitra/reseller yang menjual ulang layanan ISP Induk.</p>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" checked={form.reseller_enabled} onChange={(e) => setField("reseller_enabled", e.target.checked)} className="sr-only peer" data-testid="reseller-enabled-toggle" />
                  <div className="w-10 h-5 bg-slate-700 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>

              <LogoUploader label="Logo Reseller / Mitra" valueKey="reseller_logo" accent="indigo" testid="reseller-logo" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Nama Brand Reseller</label>
                  <input type="text" data-testid="input-reseller-name" value={form.reseller_nama} onChange={(e) => setField("reseller_nama", e.target.value)} placeholder="DEF Net" className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Badan Usaha / PT / CV</label>
                  <input type="text" data-testid="input-reseller-badan" value={form.reseller_badan_usaha} onChange={(e) => setField("reseller_badan_usaha", e.target.value)} placeholder="PT. DEF" className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Alamat Kantor Reseller</label>
                <textarea rows="2" data-testid="input-reseller-alamat" value={form.reseller_alamat} onChange={(e) => setField("reseller_alamat", e.target.value)} className={inputCls} />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">No. Telepon</label>
                  <input type="text" data-testid="input-reseller-telepon" value={form.reseller_telepon} onChange={(e) => setField("reseller_telepon", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">No. WhatsApp</label>
                  <input type="text" data-testid="input-reseller-wa" value={form.reseller_whatsapp} onChange={(e) => setField("reseller_whatsapp", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Email</label>
                  <input type="email" data-testid="input-reseller-email" value={form.reseller_email} onChange={(e) => setField("reseller_email", e.target.value)} className={inputCls} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">Website</label>
                  <input type="text" data-testid="input-reseller-website" value={form.reseller_website} onChange={(e) => setField("reseller_website", e.target.value)} className={inputCls} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1">NPWP</label>
                  <input type="text" data-testid="input-reseller-npwp" value={form.reseller_npwp} onChange={(e) => setField("reseller_npwp", e.target.value)} className={inputCls} />
                </div>
              </div>
            </div>
          )}

          <div className="pt-4 flex justify-end border-t border-slate-800">
            <button
              type="submit"
              data-testid="save-perusahaan-btn"
              className="px-5 py-2.5 bg-gradient-to-r from-teal-600 to-emerald-600 hover:from-teal-500 hover:to-emerald-500 text-white font-bold text-xs rounded-xl shadow-lg shadow-teal-600/30 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              <span>Simpan Pengaturan Perusahaan</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};
